import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import ResultsList from '../../../components/resultCard'
import ResultsListDesk from '../../../components/resultsListDesk'
import { Button } from '../../../components/ui/button'
import { Section, SectionHeader, SectionTitle } from '../../../styled/sections'
import theme from '../../../styled/theme'
import Feedback from '../../../components/feedback'
import Input from '../../../components/ui/input'
import { Formik } from 'formik'
import { resetLabsFilters, setLabsFilters } from '../../../actions/filterLaboratory'
import { Filter, Loader, Edit, Trash, Info } from '../../../assets/icons'
import Pagination from '../../../components/pagination'
import { FormContainer } from '../../../components/ui/form'
import { USERS_FILTERS } from '../../../constants'
import { selectLabsFilters, selectLabsFiltersCount } from '../../../reducers/filterLaboratory'
import UserRolesFilterModal from './UserRolesFilterModal'
import useIsMobile from '../../../hooks/useIsMobile'
import { blankPassword, deleteUser, getUsers } from '../../../api'
import to from 'await-catch'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { addFlashMessage, hideLoader, showLoader } from '../../../actions'
import { Text2, Text1 } from '../../../components/ui/text'

const DEFAULT_ROLE_FILTERS = USERS_FILTERS.map(item => item.key)
const USER_ACTION_PASSWORD = 'password'
const USER_ACTION_DELETE = 'delete'

function UserAdmin({
	filters,
	setLabsFilters,
	showLoader,
	hideLoader,
	addFlashMessage
}) {
	const [showFiltersModal, setShowFiltersModal] = useState(false)
	const [selectedRoles, setSelectedRoles] = useState(DEFAULT_ROLE_FILTERS)
	const isMobile = useIsMobile()
	const [dataList, setDataList] = useState([]);
	const [pagination, setPagination] = useState({ totalPages: 1, currentPage: 1 });
	const [search, setSearch] = useState('')
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		action: null,
		user: null,
		submitting: false
	})
	const [feedbackState, setFeedbackState] = useState({
		show: false,
		title: '',
		isError: false
	})
	const history = useHistory();

	useEffect(() => {
		if (Array.isArray(filters?.roles) && filters.roles.length > 0) {
			setSelectedRoles(filters.roles)
		}
	}, [filters])

	const handlePageChange = page => {
		setPagination(prev => ({ ...prev, currentPage: page }))
	}

	const handleOpenFilters = () => {
		setShowFiltersModal(true)
	}

	const handleApplyFilters = roles => {
		const safeRoles = roles?.length ? roles : DEFAULT_ROLE_FILTERS
		setSelectedRoles(safeRoles)
		setLabsFilters({
			...filters,
			roles: safeRoles
		})
		setShowFiltersModal(false)
	}

	const handleCreateUser = () => {
		history.push('/inicio/usuarios/nuevo');
	}

	const mapUsuario = (data) => {
		return {
			idUsuario: data.idUsuario,
			user: `${data.nombre} ${data.apellido}`,
			email: data.email,
			roles: data.gruposRoles && data.gruposRoles.length > 0
				? data.gruposRoles[0].nombre
				: undefined,
			empresa: data.metadatoValores?.[0]?.valor
		}
	}

	const fetchData = async () => {
		try {
			showLoader()
			const [err, { data } = {}] = await to(getUsers({
				gruposRoles: selectedRoles,
				itemsPorPagina: 10,
				pagina: pagination.currentPage,
				totalPaginas: pagination.totalPages,
				items: [],
				buscarUsuario: { EMAIL: search }
			}))

			if (err)
				throw new Error("Error en el fetch de usuarios");

			setDataList(data.items.map(mapUsuario));
			setPagination({ ...pagination, totalPages: data.totalPaginas })
		} catch (error) {
			addFlashMessage("Ocurrió un error cargando los usuarios");
		}
		finally {
			hideLoader()
		}
	};

	const handleEditUser = row => {
		history.push(`/inicio/usuarios/modificar?idUsuario=${row.idUsuario}`, { idUsuario: row.idUsuario })
	}

	const openConfirmModal = (row, action) => {
		setConfirmModal({
			show: true,
			action,
			user: row,
			submitting: false
		})
	}

	const closeConfirmModal = () => {
		setConfirmModal({
			show: false,
			action: null,
			user: null,
			submitting: false
		})
	}

	const handleConfirmAction = async () => {
		if (!confirmModal.user || !confirmModal.action) return

		setConfirmModal(prev => ({ ...prev, submitting: true }))
		showLoader()
		try {
			if (confirmModal.action === USER_ACTION_PASSWORD) {
				const [err, { data } = {}] = await to(blankPassword(confirmModal.user.email))
				if (err) {
					addFlashMessage('Ocurrió un error al blanquear la contraseña')
					return
				}

				setFeedbackState({
					show: true,
					title: data || 'La contraseña fue blanqueada correctamente.',
					isError: false
				})
			}

			if (confirmModal.action === USER_ACTION_DELETE) {
				const [err] = await to(deleteUser(confirmModal.user.idUsuario))
				if (err) {
					addFlashMessage('Ocurrió un error al eliminar el usuario')
					return
				}

				setFeedbackState({
					show: true,
					title: 'El usuario fue dado de baja correctamente.',
					isError: false
				})
				fetchData()
			}
		} finally {
			hideLoader()
			closeConfirmModal()
		}
	}

	useEffect(() => {
		const delayDebounce = setTimeout(() => {
			fetchData()
		}, 500)

		return () => clearTimeout(delayDebounce)
	}, [search, selectedRoles, pagination.currentPage])

	useEffect(() => {
		setPagination(prev => ({ ...prev, currentPage: 1 }))
	}, [search, selectedRoles])

	const userFields = [
		{ key: 'roles', label: 'Rol' },
		{ key: 'user', label: 'Nombre y apellido' },
		{ key: 'email', label: 'Email' },
		{ key: 'empresa', label: 'Empresa' },
		{ key: 'acciones', label: 'Acciones' }
	]

	const isDeleteAction = confirmModal.action === USER_ACTION_DELETE
	const confirmModalTitle = isDeleteAction
		? `¿Estás seguro que querés eliminar el usuario “${confirmModal.user?.email || ''}”?`
		: `¿Estás seguro que querés blanquear la contraseña para el usuario “${confirmModal.user?.email || ''}”?`

	if (feedbackState.show) {
		return (
			<Feedback
				title={feedbackState.title}
				isError={feedbackState.isError}
				buttonLabel={'Volver'}
				onClickButton={() => setFeedbackState({ show: false, title: '', isError: false })}
			/>
		)
	}

	return (
		<div style={{ width: '100%' }}>
			<Section gap="32px">
				<SectionHeader direction="row" align={'flex-start'} gap="12px">
					<SectionTitle mb="0px" mbDesktop="0px">
						Listado de Usuarios{' '}
					</SectionTitle>
					{!isMobile && (
						<Button
							color={theme.colors.text.secondaryText}
							$variant={'primary'}
							label={`Crear nuevo usuario `}
							onClick={handleCreateUser}
						/>
					)}
				</SectionHeader>
				<SectionHeader align={'flex-start'}>
					<Text2 color={theme.colors.text.secondaryText}>Buscar por nombre o email </Text2>
				</SectionHeader>
				<Formik
					initialValues={{
						nombre: ''
					}}
					enableReinitialize
				>
					{() => (
						<FormContainer direction="row" align="start" justify="start" margin="0">
							<Input
								name="nombre"
								placeholder="Buscar por nombre o email"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
							<Button
								color={theme.colors.text.secondaryText}
								$variant={'cardNav'}
								iconLeft={<Filter />}
								label={`Filtros `}
								onClick={handleOpenFilters}
							/>
						</FormContainer>
					)}
				</Formik>
			</Section>

			<Section>
				{isMobile ? (
					<ResultsList
						results={dataList}
						fields={userFields}
						renderField={(field, result) => {
							if (field.key === 'roles') {
								return <RoleBadge key={field.key}>{result.roles || '-'}</RoleBadge>
							}

							if (field.key === 'user') {
								return (
									<Text1 key={field.key} size={'24px'} color={theme.colors.text.primaryText}>
										{result.user}
									</Text1>
								)
							}

							if (field.key === 'email' || field.key === 'empresa') {
								return (
									<Text1 key={field.key} size={'18px'} color={theme.colors.text.secondaryText}>
										{result[field.key] || '-'}
									</Text1>
								)
							}

							if (field.key === 'acciones') {
								return (
									<ActionCell key={field.key}>
										<ActionLink onClick={() => openConfirmModal(result, USER_ACTION_PASSWORD)}>
											<Loader color={theme.colors.brand.blueOmint} />
											<Text1
												color={theme.colors.brand.blueOmint}
												size={'20px'}
												style={{ textDecoration: 'underline', cursor: 'pointer' }}
											>
												Blanquear
											</Text1>
										</ActionLink>
										<ActionLink onClick={() => handleEditUser(result)}>
											<Edit color={theme.colors.brand.blueOmint} />
											<Text1
												color={theme.colors.brand.blueOmint}
												size={'20px'}
												style={{ textDecoration: 'underline', cursor: 'pointer' }}
											>
												Editar
											</Text1>
										</ActionLink>
										<ActionLink onClick={() => openConfirmModal(result, USER_ACTION_DELETE)}>
											<Trash color={theme.colors.brand.blueOmint} />
											<Text1
												color={theme.colors.brand.blueOmint}
												size={'20px'}
												style={{ textDecoration: 'underline', cursor: 'pointer' }}
											>
												Borrar
											</Text1>
										</ActionLink>
									</ActionCell>
								)
							}

							return null
						}}
					/>
				) : (
					<ResultsListDesk
						isSearch={true}
						results={dataList}
						onEditUser={handleEditUser}
						onBlankUser={row => openConfirmModal(row, USER_ACTION_PASSWORD)}
						onDeleteUser={row => openConfirmModal(row, USER_ACTION_DELETE)}
					/>
				)}
			</Section>
			<Pagination
				totalPages={pagination.totalPages}
				currentPage={pagination.currentPage}
				onPageChange={handlePageChange}
			/>
			{isMobile && (
				<SectionHeader align={'center'} direction="row">
					<Button
						width={'100%'}
						color={theme.colors.text.secondaryText}
						$variant={'primary'}
						label={'Crear nuevo usuario'}
						onClick={handleCreateUser}
					/>
				</SectionHeader>
			)}
			<UserRolesFilterModal
				showModal={showFiltersModal}
				selectedRoles={selectedRoles}
				onClose={() => setShowFiltersModal(false)}
				onApply={handleApplyFilters}
			/>
			{confirmModal.show && (
				<ModalOverlay>
					<ModalCard>
						<IconWrap>
							<Info color={isDeleteAction ? theme.colors.feedback.error : theme.colors.brand.lightBlue} />
						</IconWrap>
						<ModalTitle>{confirmModalTitle}</ModalTitle>
						<ModalActions>
							<Button
								label={isDeleteAction ? 'Eliminar' : 'Aceptar'}
								$variant={'primary'}
								size={isMobile ? 'mobile' : 'xlarge'}
								onClick={handleConfirmAction}
								disabled={confirmModal.submitting}
							/>
							<Button
								label={'Cancelar'}
								$variant={'tertiary'}
								size={isMobile ? 'mobile' : 'xlarge'}
								onClick={closeConfirmModal}
								disabled={confirmModal.submitting}
							/>
						</ModalActions>
					</ModalCard>
				</ModalOverlay>
			)}
		</div>
	)
}

const mapStateToProps = ({ patient, filterLaboratory, auth }) => ({
	filters: selectLabsFilters(filterLaboratory),
	filtersCount: selectLabsFiltersCount(filterLaboratory),
	patient,
	auth
})

const mapDispatchToProps = {
	setLabsFilters,
	resetLabsFilters,
	showLoader,
	hideLoader,
	addFlashMessage
}

export default connect(mapStateToProps, mapDispatchToProps)(UserAdmin)

const RoleBadge = styled.span`
	align-self: flex-start;
	padding: 6px 10px;
	border-radius: 999px;
	background: ${props => props.theme.colors.brand.backgroundBlue};
	color: ${props => props.theme.colors.brand.lightBlue};
	font-size: 14px;
	font-weight: 500;
`

const ActionCell = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
	margin-top: 8px;
`

const ActionLink = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	padding: 0;
	display: inline-flex;
	align-items: center;
	gap: 6px;
`

const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1200;
	background: rgba(0, 0, 0, 0.35);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
`

const ModalCard = styled.div`
	width: 100%;
	max-width: 640px;
	background: ${props => props.theme.colors.brand.white};
	border-radius: 20px;
	padding: 48px 32px 28px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		height: 100%;
		border-radius: 20px;
		justify-content: space-between;
		padding: 48px 16px 24px;
	}
`

const IconWrap = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`

const ModalTitle = styled(Text1)`
	text-align: center;
	font-size: 24px;
	color: ${props => props.theme.colors.text.primaryText};
`

const ModalActions = styled.div`
	width: 50%;
	display: flex;
	flex-direction: column;
	gap: 8px;
`
